import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  DEFAULT_CURRENCY_CODE,
  getCurrencySymbol,
  MICRO_CONVERSION,
  roundMicrosToNearestWholeCent,
  roundToNearestHundredth,
} from '@/utils/money';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { INPUT_HEIGHT } from '@/components/override-input';
import { MenuItem, useMenuInfoContext } from '@/contexts/menu-info-context';
import {
  NativeSyntheticEvent,
  StyleProp,
  Text,
  TextInputEndEditingEventData,
  TextInputFocusEventData,
  View,
  ViewStyle,
} from 'react-native';
import { TextInput } from '@/components/text-input';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

const MAX_INPUT_WIDTH = 105;

enum PricingField {
  CostPercentage = 'CostPercentage',
  Profit = 'Profit',
  SalePrice = 'SalePrice',
}

interface MenuItemPricing {
  foodCostPercentage: number;
  profitInMicros: number;
  salePriceInMicros: number;
}

interface Props {
  menuItem: MenuItem;
  style?: StyleProp<ViewStyle>;
}

export const MenuItemPriceInputs: React.FC<Props> = ({ menuItem, style }) => {
  const [styles] = useStyles();
  const { setMenuItemPrice } = useMenuInfoContext();
  const { currencyCode } = useVariantPackInfoContext();

  const [salePriceInMicros, setSalePriceInMicros] = useState('');
  const [profitInMicros, setProfitInMicros] = useState('');
  const [foodCostPercentage, setFoodCostPercentage] = useState('');

  const calculatedPricing: MenuItemPricing | null = useMemo(() => {
    if (menuItem.plateCostInMicros && menuItem.platePriceInMicros) {
      return {
        foodCostPercentage: roundToNearestHundredth(
          (menuItem.plateCostInMicros / menuItem.platePriceInMicros) * 100,
        ),
        profitInMicros:
          menuItem.platePriceInMicros - menuItem.plateCostInMicros,
        salePriceInMicros: menuItem.platePriceInMicros,
      };
    }

    return null;
  }, [menuItem]);

  useEffect(() => {
    if (calculatedPricing) {
      setSalePriceInMicros(
        String(
          (calculatedPricing.salePriceInMicros / MICRO_CONVERSION).toFixed(2),
        ),
      );
      setProfitInMicros(
        String(
          (calculatedPricing.profitInMicros / MICRO_CONVERSION).toFixed(2),
        ),
      );
      setFoodCostPercentage(String(calculatedPricing.foodCostPercentage));
    } else {
      setProfitInMicros('');
      setFoodCostPercentage('');
    }
  }, [calculatedPricing]);

  const updateCalculatedPricing = useCallback(
    (field: PricingField) =>
      (
        event: NativeSyntheticEvent<
          TextInputFocusEventData | TextInputEndEditingEventData
        >,
      ) => {
        const value = event.nativeEvent.text;
        const numericValue = Number(value);
        if (
          isNaN(numericValue) ||
          typeof menuItem.plateCostInMicros !== 'number'
        ) {
          return;
        }

        let newPriceInMicros = calculatedPricing?.salePriceInMicros ?? 0;
        switch (field) {
          case PricingField.CostPercentage:
            setFoodCostPercentage(value);
            newPriceInMicros =
              menuItem.plateCostInMicros / (numericValue / 100);
            break;
          case PricingField.Profit:
            setProfitInMicros(value);
            newPriceInMicros =
              menuItem.plateCostInMicros + numericValue * MICRO_CONVERSION;
            break;
          case PricingField.SalePrice:
            setSalePriceInMicros(value);
            newPriceInMicros = numericValue * MICRO_CONVERSION;
            break;
        }

        setMenuItemPrice(
          roundMicrosToNearestWholeCent(newPriceInMicros),
          menuItem.uuid,
        );
      },
    [calculatedPricing, menuItem, setMenuItemPrice],
  );

  return menuItem.ingredients.length ? (
    <View style={[styles.row, style]}>
      <View style={styles.priceColumn}>
        <Text style={[TYPOGRAPHY.p, styles.priceLabel]}>
          {i18n.t('menu_item.sale_price')}
        </Text>
        <TextInput
          containerStyle={styles.input}
          keyboardType="decimal-pad"
          leftElement={
            <Text style={[TYPOGRAPHY.small, styles.inputElements]}>
              {getCurrencySymbol(currencyCode ?? DEFAULT_CURRENCY_CODE)}
            </Text>
          }
          onBlur={updateCalculatedPricing(PricingField.SalePrice)}
          onChangeText={setSalePriceInMicros}
          onEndEditing={updateCalculatedPricing(PricingField.SalePrice)}
          showDoneButtonAccessoryView
          style={[TYPOGRAPHY.small, FONTS.primaryBold, styles.rightAligned]}
          value={salePriceInMicros}
        />
      </View>
      <View style={styles.priceColumn}>
        <Text style={[TYPOGRAPHY.p, styles.priceLabel]}>
          {i18n.t('menu_item.profit_per_plate')}
        </Text>
        <TextInput
          containerStyle={styles.input}
          keyboardType="decimal-pad"
          leftElement={
            <Text style={[TYPOGRAPHY.small, styles.inputElements]}>
              {getCurrencySymbol(currencyCode ?? DEFAULT_CURRENCY_CODE)}
            </Text>
          }
          onBlur={updateCalculatedPricing(PricingField.Profit)}
          onChangeText={setProfitInMicros}
          onEndEditing={updateCalculatedPricing(PricingField.Profit)}
          showDoneButtonAccessoryView
          style={[TYPOGRAPHY.small, FONTS.primaryBold, styles.rightAligned]}
          value={profitInMicros}
        />
      </View>
      <View style={styles.priceColumn}>
        <Text style={[TYPOGRAPHY.p, styles.priceLabel]}>
          {i18n.t('menu_item.food_cost')}
        </Text>
        <TextInput
          containerStyle={styles.input}
          keyboardType="decimal-pad"
          onBlur={updateCalculatedPricing(PricingField.CostPercentage)}
          onChangeText={setFoodCostPercentage}
          onEndEditing={updateCalculatedPricing(PricingField.CostPercentage)}
          rightElement={
            <Text style={[TYPOGRAPHY.small, styles.inputElements]}>{'%'}</Text>
          }
          showDoneButtonAccessoryView
          style={[TYPOGRAPHY.small, FONTS.primaryBold]}
          value={foodCostPercentage}
        />
      </View>
    </View>
  ) : null;
};

const useStyles = createStyles((theme) => ({
  input: {
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.stroke,
    borderRadius: 4,
    borderWidth: 2,
    height: INPUT_HEIGHT,
    width: MAX_INPUT_WIDTH,
  },
  inputElements: {
    ...FONTS.primarySemiBold,
    color: theme.colors.inactiveText,
    lineHeight: 16,
  },
  priceColumn: {
    flexDirection: 'column',
    gap: 4,
  },
  priceLabel: {
    fontSize: 14,
  },
  rightAligned: {
    textAlign: 'right',
  },
  row: {
    flexDirection: 'row',
  },
}));
